import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  FileText,
  Search,
  Filter,
  MoreHorizontal,
  Trash2,
  Copy,
  ChevronRight,
  Star,
  AlertCircle,
  CheckCircle2,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  specDocumentService,
  type SpecDocument,
  type SpecDocumentCategory,
  type SpecDocumentStatus,
} from '@/services/specDocumentService';
import { artifactService, ARTIFACT_CREATED_EVENT, type ArtifactEventDetail } from '@/services/artifactService';
import type { WorkflowStatusResponse, CanCreateResponse } from '@/types';

interface SpecDocumentListProps {
  projectId: string;
  selectedDocumentId?: string;
  onSelectDocument: (document: SpecDocument) => void;
  onCreateDocument?: (document: SpecDocument) => void;
  className?: string;
}

const STATUS_COLORS: Record<SpecDocumentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  deprecated: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<SpecDocumentStatus, string> = {
  draft: '초안',
  review: '검토 중',
  approved: '승인됨',
  deprecated: '폐기됨',
};

export function SpecDocumentList({
  projectId,
  selectedDocumentId,
  onSelectDocument,
  onCreateDocument,
  className,
}: SpecDocumentListProps) {
  const [documents, setDocuments] = useState<SpecDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SpecDocumentCategory | 'all'>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<SpecDocumentCategory>('prd');
  const [isCreating, setIsCreating] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatusResponse | null>(null);
  const [canCreateInfo, setCanCreateInfo] = useState<CanCreateResponse | null>(null);
  const [isCheckingCanCreate, setIsCheckingCanCreate] = useState(false);

  const loadDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const filters: { category?: SpecDocumentCategory; search?: string } = {};
      if (categoryFilter !== 'all') {
        filters.category = categoryFilter;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }
      const response = await specDocumentService.getDocuments(projectId, filters);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, categoryFilter, searchQuery]);

  const loadWorkflowStatus = useCallback(async () => {
    try {
      const status = await artifactService.getWorkflowStatus(projectId);
      setWorkflowStatus(status);
    } catch (error) {
      console.error('Failed to load workflow status:', error);
    }
  }, [projectId]);

  useEffect(() => {
    loadDocuments();
    loadWorkflowStatus();
  }, [loadDocuments, loadWorkflowStatus]);

  // Check if selected category can be created when dialog opens or category changes
  useEffect(() => {
    if (isCreateDialogOpen && selectedCategory) {
      const checkCanCreate = async () => {
        setIsCheckingCanCreate(true);
        try {
          const info = await artifactService.canCreateCategory(projectId, selectedCategory);
          setCanCreateInfo(info);
        } catch (error) {
          console.error('Failed to check can create:', error);
          setCanCreateInfo(null);
        } finally {
          setIsCheckingCanCreate(false);
        }
      };
      checkCanCreate();
    }
  }, [isCreateDialogOpen, selectedCategory, projectId]);

  // 아티팩트 생성 이벤트 리스닝 (채팅에서 문서 생성 시 자동 새로고침)
  useEffect(() => {
    const handleArtifactCreated = (event: Event) => {
      const detail = (event as CustomEvent<ArtifactEventDetail>).detail;
      // 현재 프로젝트의 아티팩트인 경우에만 새로고침
      if (!detail?.projectId || detail.projectId === projectId) {
        loadDocuments();
        loadWorkflowStatus();
      }
    };

    window.addEventListener(ARTIFACT_CREATED_EVENT, handleArtifactCreated);
    return () => {
      window.removeEventListener(ARTIFACT_CREATED_EVENT, handleArtifactCreated);
    };
  }, [projectId, loadDocuments, loadWorkflowStatus]);

  // Get workflow info for a category
  const getCategoryWorkflowInfo = (category: SpecDocumentCategory) => {
    if (!workflowStatus) return null;
    return workflowStatus.workflow_status.find(s => s.category === category);
  };

  const handleCreateFromTemplate = async () => {
    // Don't allow creation if dependencies are not met
    if (canCreateInfo && !canCreateInfo.can_create) {
      return;
    }

    try {
      setIsCreating(true);
      const newDoc = await specDocumentService.createFromTemplate(projectId, selectedCategory);
      setIsCreateDialogOpen(false);
      setCanCreateInfo(null);
      await loadDocuments();
      await loadWorkflowStatus(); // Refresh workflow status
      onSelectDocument(newDoc);
      onCreateDocument?.(newDoc);
    } catch (error) {
      console.error('Failed to create document:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await specDocumentService.deleteDocument(projectId, documentId);
      await loadDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleDuplicateDocument = async (documentId: string) => {
    try {
      const duplicatedDoc = await specDocumentService.duplicateDocument(projectId, documentId);
      await loadDocuments();
      onSelectDocument(duplicatedDoc);
    } catch (error) {
      console.error('Failed to duplicate document:', error);
    }
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([loadDocuments(), loadWorkflowStatus()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadDocuments, loadWorkflowStatus]);

  const categories = specDocumentService.getCategories();

  return (
    <div className={cn('flex flex-col h-full border-r bg-muted/30', className)}>
      {/* Header */}
      <div className="p-3 border-b space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm">스펙 문서</h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="새로고침"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsCreateDialogOpen(true)}
              title="새 문서 만들기"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="문서 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        {/* Category Filter */}
        <Select
          value={categoryFilter}
          onValueChange={(value) => setCategoryFilter(value as SpecDocumentCategory | 'all')}
        >
          <SelectTrigger className="h-9">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="카테고리 필터" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">전체 카테고리</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {specDocumentService.getCategoryIcon(cat.value)} {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Document List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-3 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : documents.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">문서가 없습니다</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              새 문서 만들기
            </Button>
          </div>
        ) : (
          <TooltipProvider>
            <div className="p-2 space-y-1">
              {documents.map((doc) => {
                const workflowInfo = getCategoryWorkflowInfo(doc.category || 'other');
                const isEssential = workflowInfo?.essential ?? false;

                return (
                  <div
                    key={doc.id}
                    className={cn(
                      'group flex items-start gap-2 p-2 rounded-md cursor-pointer transition-colors',
                      'hover:bg-accent',
                      selectedDocumentId === doc.id && 'bg-accent'
                    )}
                    onClick={() => onSelectDocument(doc)}
                  >
                    <span className="text-lg shrink-0">
                      {specDocumentService.getCategoryIcon(doc.category || 'other')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{doc.title}</span>
                        {isEssential && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>필수 문서</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className={cn('text-xs', STATUS_COLORS[doc.status])}>
                          {STATUS_LABELS[doc.status]}
                        </Badge>
                        {/* Confirmation Badge for Essential Documents */}
                        {isEssential && (
                          workflowInfo?.confirmed ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  확인됨
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-medium">{workflowInfo.confirmed_by_name}님이 확인</p>
                                {workflowInfo.confirmed_at && (
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(workflowInfo.confirmed_at).toLocaleString('ko-KR')}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  미확인
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>아직 확인되지 않은 필수 문서입니다</p>
                              </TooltipContent>
                            </Tooltip>
                          )
                        )}
                        <span className="text-xs text-muted-foreground">v{doc.version}</span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDuplicateDocument(doc.id)}>
                          <Copy className="mr-2 h-4 w-4" />
                          복제
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          삭제
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </ScrollArea>

      {/* Create Document Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) {
          setCanCreateInfo(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>새 스펙 문서</DialogTitle>
            <DialogDescription>
              문서 카테고리를 선택하세요. 필수 문서는 순서에 따라 생성해야 합니다.
            </DialogDescription>
          </DialogHeader>

          {/* Workflow Progress Summary */}
          {workflowStatus && (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">필수 문서 진행률</span>
                <span className="font-medium">
                  {workflowStatus.summary.completed} / {workflowStatus.summary.total_essential}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary rounded-full h-2 transition-all"
                  style={{
                    width: `${(workflowStatus.summary.completed / workflowStatus.summary.total_essential) * 100}%`,
                  }}
                />
              </div>
              {workflowStatus.summary.all_essential_complete ? (
                <div className="flex items-center gap-2 text-xs text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  모든 필수 문서가 완료되었습니다
                </div>
              ) : workflowStatus.summary.can_create_next.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  다음 생성 가능: {workflowStatus.summary.can_create_next.map(cat => {
                    const info = getCategoryWorkflowInfo(cat);
                    return info?.title || cat;
                  }).join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="py-2 space-y-4">
            <div>
              <Label htmlFor="category">문서 카테고리</Label>
              <Select
                value={selectedCategory}
                onValueChange={(value) => setSelectedCategory(value as SpecDocumentCategory)}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Essential Documents Group */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                    필수 문서
                  </div>
                  {categories.filter(cat => artifactService.isEssentialCategory(cat.value)).map((cat) => {
                    const workflowInfo = getCategoryWorkflowInfo(cat.value);
                    const exists = workflowInfo?.exists ?? false;
                    const canCreate = workflowInfo?.can_create ?? true;

                    return (
                      <SelectItem
                        key={cat.value}
                        value={cat.value}
                        disabled={exists || !canCreate}
                        className="relative"
                      >
                        <div className="flex items-center gap-2">
                          <span>{specDocumentService.getCategoryIcon(cat.value)}</span>
                          <span>{cat.label}</span>
                          {exists && (
                            <CheckCircle2 className="h-3 w-3 text-green-500 ml-auto" />
                          )}
                          {!exists && !canCreate && (
                            <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}

                  {/* Optional Documents Group */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                    선택 문서
                  </div>
                  {categories.filter(cat => !artifactService.isEssentialCategory(cat.value)).map((cat) => {
                    const workflowInfo = getCategoryWorkflowInfo(cat.value);
                    const exists = workflowInfo?.exists ?? false;
                    const canCreate = workflowInfo?.can_create ?? true;

                    return (
                      <SelectItem
                        key={cat.value}
                        value={cat.value}
                        disabled={exists || !canCreate}
                      >
                        <div className="flex items-center gap-2">
                          <span>{specDocumentService.getCategoryIcon(cat.value)}</span>
                          <span>{cat.label}</span>
                          {exists && (
                            <CheckCircle2 className="h-3 w-3 text-green-500 ml-auto" />
                          )}
                          {!exists && !canCreate && (
                            <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Can Create Info / Dependency Warning */}
            {isCheckingCanCreate ? (
              <div className="text-sm text-muted-foreground">의존성 확인 중...</div>
            ) : canCreateInfo && (
              <>
                {canCreateInfo.can_create ? (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      이 문서를 생성할 수 있습니다.
                      {canCreateInfo.essential && (
                        <span className="ml-1">(필수 문서)</span>
                      )}
                    </AlertDescription>
                  </Alert>
                ) : canCreateInfo.reason === 'already_exists' ? (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-700">
                      {canCreateInfo.message || '이 카테고리의 문서가 이미 존재합니다.'}
                    </AlertDescription>
                  </Alert>
                ) : canCreateInfo.reason === 'missing_dependencies' ? (
                  <Alert className="border-red-200 bg-red-50">
                    <Lock className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      <div className="font-medium mb-1">선행 문서가 필요합니다:</div>
                      <ul className="list-disc list-inside text-sm">
                        {canCreateInfo.missing_dependency_names?.map((name, i) => (
                          <li key={i}>{name}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : null}
              </>
            )}

            <p className="text-xs text-muted-foreground">
              템플릿을 기반으로 새 문서가 생성됩니다.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              취소
            </Button>
            <Button
              onClick={handleCreateFromTemplate}
              disabled={isCreating || isCheckingCanCreate || canCreateInfo?.can_create === false}
            >
              {isCreating ? '생성 중...' : '문서 생성'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SpecDocumentList;
